import React, { useEffect, useMemo, useState } from 'react';
import { DocumentChartBarIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from '../../helpers/useDebounce';
import { arrayHasItems } from '../../helpers/common';
import { ROUTES, SORT_DIRECTION } from '../../consts/general';
import {
  getAllPC,
  getTotalInventoryValues,
  ProductCostsLoadingKeys,
} from '../../slices/productCostSlice';
import {
  productCostsTableHeaders,
  productCostsTableHiddenHeaders,
} from '../../consts/productCosts';
import ReactTooltip from 'react-tooltip';
import { IN_ACTIVE, PHASED_STATUSES } from '../../consts/inventoryStatus';
import axios from 'axios';
import { YES, NO } from '../../consts/common';
import { usePageReset } from '../../helpers/usePageReset';
import { EADIN_CATALOG } from '../../consts/reports';
import ReportTemplate from '../../components/reports/ReportTemplate';
import CSVExportButton from '../../components/reports/CSVExportButton';

const ALL_BOOLEAN_FILTER_VALUES = [YES, NO];
const PAGE_SIZE = 50;

const RIGHT_ALIGNED_COLUMNS = new Set([
  'unitCost',
  'shippingCost',
  'tariffCost',
  'totalLandedCost',
  'weightedCost',
  'onHand',
  'onOrder',
  'inventoryValue',
]);

const EadinCatalog = ({ marginFromSidebar }) => {
  const navigate = useNavigate();
  const pageParams = window?.location?.search ? new URLSearchParams(window.location.search) : null;

  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState(pageParams?.get('search') || '');
  const [currentPage, setCurrentPage] = useState(Number(pageParams?.get('page') || 1));
  const [sortBy, setSortBy] = useState(pageParams?.get('sortBy') || '');
  const [sortDirection, setSortDirection] = useState(pageParams?.get('sortDirection') || '');
  const currentSearchValue = useDebounce(searchTerm, 800);

  const shouldSkipRequest = usePageReset(setCurrentPage, currentPage, 'get:/api/v1/product-costs');

  const {
    productCosts,
    pagination,
    loadingKeys: loadingKeysPO,
    loading: loadingPO,
    isFirstLoadComplete,
    totalInventoryValue,
  } = useSelector((state) => state.productCosts);

  const [orderedProducts, setOrderedProducts] = useState(productCosts || []);

  const columns = useMemo(
    () =>
      Object.values(productCostsTableHeaders)
        .filter((header) => !productCostsTableHiddenHeaders.includes(header.value))
        .map((header) => ({
          key: header.value,
          label: header.label,
          type: 'string',
          sortable: true,
          align: RIGHT_ALIGNED_COLUMNS.has(header.value) ? 'right' : 'left',
          ...(header.value === 'productName'
            ? { width: '450px', maxWidth: '450px' }
            : {}),
        })),
    []
  );

  const totalRecords = useMemo(() => {
    const match = pagination.rangeRows?.match(/ of (\d+)$/);
    return match ? Number(match[1]) : 0;
  }, [pagination.rangeRows]);

  const haveTotalHeaders = totalInventoryValue !== undefined && typeof totalInventoryValue === 'number';

  const columnSummaryRow = useMemo(() => {
    if (!haveTotalHeaders) return null;
    return {
      inventoryValue: (
        <div className="flex items-center justify-center gap-2">
          {`$${Number(totalInventoryValue)?.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`}
          {productCostsTableHeaders?.inventoryValue?.tooltip && (
            <span className="inline-flex" onClick={(e) => e.stopPropagation()}>
              <InformationCircleIcon
                className="h-4 w-4 text-gray-500"
                data-tip
                data-for="inventoryValue_total_eadin_catalog_tooltip"
              />
              <ReactTooltip
                id="inventoryValue_total_eadin_catalog_tooltip"
                html
                effect="solid"
                place="bottom"
                className="text-xs normal-case"
              >
                {productCostsTableHeaders.inventoryValue.tooltip}
              </ReactTooltip>
            </span>
          )}
        </div>
      ),
    };
  }, [haveTotalHeaders, totalInventoryValue]);

  useEffect(() => {
    ReactTooltip.rebuild();
  }, [orderedProducts, columnSummaryRow]);

  useEffect(() => {
    const currentProductCosts = arrayHasItems(productCosts) ? productCosts : [];
    setOrderedProducts(currentProductCosts);
  }, [productCosts]);

  const handleGetAllPC = () => {
    dispatch(getAllPC({
      search: currentSearchValue,
      page: currentPage,
      itemsPerPage: PAGE_SIZE,
      phasedStatuses: PHASED_STATUSES,
      amazon: ALL_BOOLEAN_FILTER_VALUES,
      isReplenishable: ALL_BOOLEAN_FILTER_VALUES,
      kit: ALL_BOOLEAN_FILTER_VALUES,
      sortBy,
      sortDirection,
    }));
  };

  useEffect(() => {
    if (currentPage === 1 && shouldSkipRequest()) {
      const params = new URLSearchParams({
        search: currentSearchValue,
        page: currentPage,
        sortBy,
        sortDirection,
      });
      navigate(`${ROUTES.REPORTS_EADIN_CATALOG}?${params}`, { replace: true });
      return;
    }

    handleGetAllPC();
    const params = new URLSearchParams({
      search: currentSearchValue,
      page: currentPage,
      sortBy,
      sortDirection,
    });
    navigate(`${ROUTES.REPORTS_EADIN_CATALOG}?${params}`, { replace: true });
  }, [
    currentSearchValue,
    currentPage,
    sortBy,
    sortDirection,
  ]);

  useEffect(() => {
    dispatch(getTotalInventoryValues());
  }, [dispatch]);

  const productCostsIsLoading = () => loadingPO && loadingKeysPO.includes(ProductCostsLoadingKeys.costs);
  const isLoading = !isFirstLoadComplete || productCostsIsLoading();

  const handleBackToReports = () => navigate(ROUTES.REPORTS);

  const handleSort = ({ field, direction }) => {
    setSortBy(field);
    setSortDirection(direction === 'asc' ? SORT_DIRECTION.ASCENDING : SORT_DIRECTION.DESCENDING);
    setCurrentPage(1);
  };

  const handleExportCSV = async () => {
    const { data } = await axios.get('/api/v1/product-costs', {
      responseType: 'blob',
      params: {
        isCSV: 'true',
        search: currentSearchValue,
        page: currentPage,
        itemsPerPage: PAGE_SIZE,
        phasedStatuses: PHASED_STATUSES,
        amazon: ALL_BOOLEAN_FILTER_VALUES,
        isReplenishable: ALL_BOOLEAN_FILTER_VALUES,
        kit: ALL_BOOLEAN_FILTER_VALUES,
        sortBy,
        sortDirection,
      },
    });
    const url = window.URL.createObjectURL(data);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${EADIN_CATALOG}_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    return { success: true, handled: true };
  };

  const breadcrumbs = [
    { label: 'Reports', icon: <DocumentChartBarIcon className="h-5 w-5 text-gray-400" /> },
    { label: '' },
  ];

  const filtersComponent = (
    <div className="contents">
      <div className="flex flex-row items-center gap-2">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          placeholder="Search products..."
          className="w-40 sm:w-[200px] rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
        />
      </div>
    </div>
  );

  const exportButton = (
    <CSVExportButton
      data={orderedProducts}
      columns={columns}
      filename={EADIN_CATALOG}
      onExport={handleExportCSV}
      disabled={isLoading}
      variant="outline"
    />
  );

  const paginationComponent =
    pagination.totalPages > 1 ? (
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-700">
          Showing {((currentPage - 1) * PAGE_SIZE) + 1} to {Math.min(currentPage * PAGE_SIZE, totalRecords)} of {totalRecords} results
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:bg-gray-100 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Previous
          </button>
          <span className="text-sm text-gray-700">
            Page {currentPage} of {pagination.totalPages}
          </span>
          <button
            onClick={() => setCurrentPage((prev) => Math.min(pagination.totalPages, prev + 1))}
            disabled={currentPage === pagination.totalPages}
            className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:bg-gray-100 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Next
          </button>
        </div>
      </div>
    ) : null;

  const renderTableRows = () =>
    (orderedProducts || []).map((product, index) => (
      <tr
        key={`${product.id}_${index}_${product.statusBeingPhasedOut}`}
        className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
      >
        <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${index % 2 === 0 ? 'bg-white' : '!bg-gray-50'}`}>
          <div className="w-12 h-12 relative rounded-md overflow-hidden bg-gray-100 border border-gray-200 flex items-center justify-center">
            <img
              src={product.productImage || 'https://placehold.co/40x40/e6e6e6/cccccc?text=No+Image'}
              alt={product.productName}
              className="max-w-full max-h-full object-contain"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = 'https://placehold.co/40x40/e6e6e6/cccccc?text=No+Image';
              }}
            />
          </div>
        </td>
        <td
          className={`px-6 py-4 text-sm text-gray-900 ${index % 2 === 0 ? 'bg-white' : '!bg-gray-50'} ${product.statusBeingPhasedOut === IN_ACTIVE ? 'text-gray-400' : ''}`}
          style={{ width: '450px', maxWidth: '450px', wordBreak: 'break-word', overflowWrap: 'break-word' }}
        >
          <div className="flex items-center flex-row gap-2">
            <span className="font-medium">{product.productName}</span>
            {product.isVirtual && (
              <>
                <InformationCircleIcon
                  className="h-5 w-5 text-gray-500 flex-shrink-0"
                  data-tip
                  data-for={`${product.id}_product_name_tooltip_${index}`}
                />
                <ReactTooltip
                  id={`${product.id}_product_name_tooltip_${index}`}
                  html
                  effect="solid"
                  place="bottom"
                  className="text-xs normal-case"
                >
                  Virtual skus do not have cost, we do not track inventory
                </ReactTooltip>
              </>
            )}
          </div>
          <div className="text-xs text-gray-500 mt-1">SKU: {product.sku}</div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {product.productBarcode || ''}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
          {product.unitCost ? `$${product.unitCost}` : 'N/A'}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
          {product.shippingCost ? `$${product.shippingCost}` : 'N/A'}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
          {product.tariffCost ? `$${product.tariffCost}` : 'N/A'}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
          {product.totalLandedCost ? `$${product.totalLandedCost}` : 'N/A'}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
          {product.weightedCost ? `$${product.weightedCost}` : 'N/A'}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
          {product.onHand || '-'}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
          {product.onOrder || '-'}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
          {product.inventoryValue ? `$${product.inventoryValue}` : 'N/A'}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
          {product.hasGroup ? 'Y' : 'N'}
        </td>
      </tr>
    ));

  return (
    <>
      <ReportTemplate
        reportId={EADIN_CATALOG}
        description="Product catalog report with costs, inventory levels, and product details"
        breadcrumbs={breadcrumbs}
        marginFromSidebar={marginFromSidebar}
        onBack={handleBackToReports}
        isLoading={isLoading}
        filters={filtersComponent}
        exportButton={exportButton}
        data={orderedProducts}
        columns={columns}
        pagination={paginationComponent}
        emptyMessage="No products match the current filters."
        onSort={handleSort}
        sortField={sortBy || null}
        sortDirection={
          sortDirection === SORT_DIRECTION.ASCENDING
            ? 'asc'
            : sortDirection === SORT_DIRECTION.DESCENDING
              ? 'desc'
              : null
        }
        columnSummaryRow={columnSummaryRow}
        isFirstColumnSticky
      >
        {renderTableRows()}
      </ReportTemplate>
    </>
  );
};

export default EadinCatalog;
